import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Package,
  Users, ShoppingBag, Clock, Truck, ShieldCheck
} from 'lucide-react';
import LoadingState from './LoadingState';
import './SalesStats.css';

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
  if (percent === 0) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontWeight="bold" fontSize="14">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Modern Color Palette
const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const PIE_COLORS = ['#6366f1', '#f472b6']; // Metal vs Stones
const ORIGIN_COLOR_MAP: Record<string, string> = {
  'Amphora (3PL)': '#eab308', // Amarillo (Yellow)
  'España (Taller)': '#9ca3af' // Gris (Gray)
};

type DateRange = '7days' | '30days' | 'thisMonth' | 'ytd' | 'custom';

interface DashboardStats {
  kpis: {
    revenue: { value: number; variation: number };
    orders: { value: number; variation: number };
    ticket: { value: number; variation: number };
    shipping: { total: number; amphora: number; taller: number; savings: number };
  };
  trend: Array<{ date: string; orders: number; amphora: number; taller: number; amphoraPercent: number; tallerPercent: number; savings: number }>;
  shippingOriginDistribution: Array<{ name: string; value: number }>;
}

export default function SalesStats() {
  const { api } = useApi();
  const { showError } = useToast();

  const [dateRange, setDateRange] = useState<DateRange>('30days');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  // Custom date state
  const [customStart, setCustomStart] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [customEnd, setCustomEnd] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Helper para obtener fechas exactas
  const getDateRangeParams = (range: DateRange) => {
    const end = new Date();
    const start = new Date();

    if (range === '7days') {
      start.setDate(end.getDate() - 7);
    } else if (range === '30days') {
      start.setDate(end.getDate() - 30);
    } else if (range === 'thisMonth') {
      start.setDate(1); // Primer día del mes
    } else if (range === 'ytd') {
      start.setMonth(0, 1); // 1 de Enero
    } else if (range === 'custom') {
      return {
        startDate: customStart || start.toISOString().split('T')[0],
        endDate: customEnd || end.toISOString().split('T')[0]
      };
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeParams(dateRange);
      const response = await api.get(`/sales/dashboard-stats?startDate=${startDate}&endDate=${endDate}`);
      setStats(response.data);
    } catch (error: any) {
      console.error('Error al cargar métricas del dashboard:', error);
      showError('Error al cargar las estadísticas de ventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardStats();
  }, [dateRange, customStart, customEnd]);

  const formatNumber = (num: number) => new Intl.NumberFormat('es-ES').format(num);
  const formatCurrency = (num: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(num);

  const renderKpiCard = (
    title: string,
    value: string,
    variation: number,
    Icon: React.ElementType,
    iconColorClass: string
  ) => {
    const isPositive = variation >= 0;
    return (
      <div className="modern-kpi-card animate-fade-in">
        <div className="kpi-header">
          <div className={`kpi-icon-wrapper ${iconColorClass}`}>
            <Icon size={24} />
          </div>
          <span className={`kpi-trend ${isPositive ? 'trend-up' : 'trend-down'}`}>
            {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {Math.abs(variation).toFixed(1)}%
          </span>
        </div>
        <div className="kpi-body">
          <h3 className="kpi-title">{title}</h3>
          <p className="kpi-value">{value}</p>
        </div>
      </div>
    );
  };

  if (loading && !stats) {
    return (
      <div className="sales-dashboard-container p-6">
        <LoadingState message="Cargando panel analítico..." />
      </div>
    );
  }

  return (
    <div className="sales-dashboard-container">
      {/* Header and Controls */}
      <div className="dashboard-header animate-fade-in">
        <div>
          <h2 className="dashboard-title">Rendimiento de Ventas</h2>
          <p className="dashboard-subtitle">Resumen ejecutivo del desempeño comercial y salidas de inventario</p>
        </div>
        <div className="dashboard-controls">
          <div className="date-range-selector">
            <button
              className={`range-btn ${dateRange === '7days' ? 'active' : ''}`}
              onClick={() => setDateRange('7days')}
            >
              7 Días
            </button>
            <button
              className={`range-btn ${dateRange === '30days' ? 'active' : ''}`}
              onClick={() => setDateRange('30days')}
            >
              30 Días
            </button>
            <button
              className={`range-btn ${dateRange === 'thisMonth' ? 'active' : ''}`}
              onClick={() => setDateRange('thisMonth')}
            >
              Este Mes
            </button>
            <button
              className={`range-btn ${dateRange === 'ytd' ? 'active' : ''}`}
              onClick={() => setDateRange('ytd')}
            >
              YTD
            </button>
            <button
              className={`range-btn ${dateRange === 'custom' ? 'active' : ''}`}
              onClick={() => setDateRange('custom')}
            >
              Personalizado
            </button>
          </div>
          {dateRange === 'custom' && (
            <div className="date-range-selector" style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
               <span className="text-sm font-medium text-gray-500">Desde:</span>
               <input 
                 type="date" 
                 value={customStart} 
                 onChange={(e) => setCustomStart(e.target.value)} 
                 className="px-2 py-1 text-sm border rounded-md"
               />
               <span className="text-sm font-medium text-gray-500">Hasta:</span>
               <input 
                 type="date" 
                 value={customEnd} 
                 onChange={(e) => setCustomEnd(e.target.value)} 
                 className="px-2 py-1 text-sm border rounded-md"
               />
            </div>
          )}
        </div>
      </div>

      {stats && (
        <>
          {/* KPI Grid */}
          <div className="dashboard-kpi-grid">
            {renderKpiCard(
              'Ingresos Estimados',
              formatCurrency(stats.kpis.revenue.value),
              stats.kpis.revenue.variation,
              DollarSign,
              'bg-indigo-light text-indigo'
            )}
            {renderKpiCard(
              'Total Pedidos',
              formatNumber(stats.kpis.orders.value),
              stats.kpis.orders.variation,
              ShoppingBag,
              'bg-amber-light text-amber'
            )}
            {renderKpiCard(
              'Ticket Medio (Aprox)',
              formatCurrency(stats.kpis.ticket.value),
              stats.kpis.ticket.variation,
              Users,
              'bg-rose-light text-rose'
            )}
          </div>

          {/* Logistics KPI Grid */}
          <div className="dashboard-kpi-grid mt-6">
            {renderKpiCard(
              'Gasto Total Envíos',
              formatCurrency(stats.kpis.shipping.total),
              0, // No variation for now
              Truck,
              'bg-gray-100 text-gray-700'
            )}
            {renderKpiCard(
              'Gasto de España (25€/ud)',
              formatCurrency(stats.kpis.shipping.taller),
              0,
              Package,
              'bg-gray-100 text-gray-500' // Gris
            )}
            {renderKpiCard(
              'Gasto de Amphora (5€/ud)',
              formatCurrency(stats.kpis.shipping.amphora),
              0,
              ShieldCheck,
              'bg-yellow-100 text-yellow-600' // Amarillo
            )}
          </div>

          {/* Main Charts Grid */}
          <div className="dashboard-charts-main">
            {/* Trend Chart */}
            <div className="modern-chart-card full-width-chart animate-fade-in">
              <div className="chart-header">
                <h3>Evolución de Pedidos Generados</h3>
              </div>
              <div className="chart-body" style={{ width: '100%', height: '350px' }}>
                <ResponsiveContainer width="99%" height="100%">
                  <AreaChart data={stats.trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6b7280', fontSize: 12 }} 
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth()+1}`;
                      }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="orders" 
                      stroke="#6366f1" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorOrders)" 
                      name="Pedidos"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Shipping Origin Pie Chart */}
            <div className="modern-chart-card animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <div className="chart-header">
                <h3>Pedidos por Origen de Envío</h3>
              </div>
              <div className="chart-body" style={{ width: '100%', height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {stats.shippingOriginDistribution && stats.shippingOriginDistribution.length > 0 ? (
                  <ResponsiveContainer width="99%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.shippingOriginDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={5}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomizedLabel}
                      >
                        {stats.shippingOriginDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={ORIGIN_COLOR_MAP[entry.name] || '#8b5cf6'} />
                        ))}
                      </Pie>
                      <Tooltip 
                         contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="empty-chart-state text-gray-400">Sin datos de origen</div>
                )}
                {/* Custom Legend Floating in Center */}
                {stats.shippingOriginDistribution && stats.shippingOriginDistribution.length > 0 && (
                   <div className="pie-center-legend absolute text-center pointer-events-none" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
                     <span className="block text-2xl font-bold text-gray-800">{formatNumber(stats.shippingOriginDistribution.reduce((acc, curr) => acc + curr.value, 0))}</span>
                     <span className="block text-xs text-gray-500 uppercase tracking-widest">Total</span>
                   </div>
                 )}
              </div>
            </div>
          </div>

          {/* Bottom Full-Width Chart */}
          <div className="dashboard-charts-bottom mt-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {/* Origin Trend Full Width Chart */}
            <div className="modern-chart-card">
              <div className="chart-header">
                <h3>Evolución de Orígenes de Envío (Amphora vs Taller)</h3>
              </div>
              <div className="chart-body" style={{ width: '100%', height: '400px' }}>
                <ResponsiveContainer width="99%" height="100%">
                  <BarChart data={stats.trend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6b7280', fontSize: 12 }} 
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth()+1}`;
                      }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      formatter={(value: any, name: string, props: any) => {
                        if (name === 'Amphora') return [`${value} pedidos (${props.payload.amphoraPercent.toFixed(1)}%)`, name];
                        if (name === 'Seekers (Taller)') return [`${value} pedidos (${props.payload.tallerPercent.toFixed(1)}%)`, name];
                        return [value, name];
                      }}
                    />
                    <Legend verticalAlign="top" height={36} iconType="circle" />
                    <Bar dataKey="amphora" stackId="a" fill="#eab308" name="Amphora" radius={[0, 0, 4, 4]} />
                    <Bar dataKey="taller" stackId="a" fill="#9ca3af" name="Seekers (Taller)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Savings Trend Chart */}
            <div className="modern-chart-card full-width-chart animate-fade-in mt-6">
              <div className="chart-header flex justify-between items-center">
                <div>
                   <h3 className="text-emerald-600">Ahorro Neto Logístico (Uso 3PL)</h3>
                   <p className="text-xs text-gray-500 mt-1">Rentabilidad bruta diaria por envíos a 5€ vs 25€</p>
                </div>
                <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-bold text-sm">
                   +{formatCurrency(stats.kpis.shipping.savings)} Total Agrupado
                </div>
              </div>
              <div className="chart-body" style={{ width: '100%', height: '350px' }}>
                <ResponsiveContainer width="99%" height="100%">
                  <AreaChart data={stats.trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6b7280', fontSize: 12 }} 
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth()+1}`;
                      }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickFormatter={(val) => `${val}€`}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      formatter={(value: any) => [`+${value}€ Cosechados`, 'Ahorro Bruto']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="savings" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorSavings)" 
                      name="Ahorro Bruto"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
}

